// 接入Sentry监控
import * as Sentry from '@sentry/browser';

// 从package.json中获取版本号
import { version } from '../../../package.json';

/** 需要屏蔽的错误 */
const ignoreErrorsReg: RegExp[] = [
  /ResizeObserver loop/i, // 带这个关键字的错误是组件内部的 warning，不影响，但是报成了 error
  /Non-Error exception captured with keys: currentTarget, isTrusted, target, type/i, // 第三方包报的警告，不影响应用运行
  /Error: 用户没有权限（令牌、用户名、密码错误）。/i, // 未登录，是状态，不是错误
  /Error: unknown error/i, // 未知错误
  /Unexpected token '<'/i, // 版本更新历史文件无法加载
  /TypeError: undefined is not an object (evaluating 'a.L')/i, // 兼容性错误
  /TypeError: undefined is not an object \(.*\)/i,
  /TypeError: null is not an object (evaluating 'this._logicalFormForControl(e).formElement')/i,
  /TypeError: null is not an object \(.*\)/i,
  /TypeError: Cannot read properties of undefined (reading 'sale')/i,
  /TypeError: Cannot read properties of undefined \(.*\)/i,
  /TypeError: Failed to fetch/i,
  /SecurityError: .*/i,
  /TypeError: .* is not a function/i,
  /NotFoundError: Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node./i,
];

/** 允许上报的域名 */
const allowUrlsReg = [/^http(s|):\/\/back\.bluemediagroup\.cn\/.*$/];

/** 是否需要忽略此信息上报 */
export const isIgnoreError = (msg: string = '') => {
  return ignoreErrorsReg.some((reg) => reg.test(msg));
};

/** 当前域名是否可上报 */
export const isAllowUrl = (url: string = '') => {
  return allowUrlsReg.some((reg) => reg.test(url));
};

export const initSentry = () => {
  /** 初始化Sentry监控 */
  Sentry.init({
    dsn: 'https://abe879e012d042a3940981add16eadc4@o4505418729521152.ingest.sentry.io/4505418998218752',
    integrations: [new Sentry.BrowserProfilingIntegration(), new Sentry.Replay()],
    environment: process.env.NODE_ENV,
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!

    // 发送错误前可进行劫持
    beforeSend: function (event, hint) {
      // 报错信息中添加版本号
      event.tags = event.tags || {};
      event.tags.version = version;

      const errMsg = event?.exception?.values?.[0]?.value;
      /**
       * 把不能正常解析的错误忽略掉
       * 参见: https://segmentfault.com/q/1010000043642498
       *
       * Non-Error promise rejection captured with keys: errorFields, outOfDate, values
       * Sentry 自定义错误，使用 Promise 发生错误没有 catch 的时候，Sentry 中通过 unhandledrejection 进行捕获
       */
      const messages = [
        'Non-Error exception captured',
        'Object captured as promise rejection with keys: errorFields, outOfDate, values',
        'Object captured as exception with keys: errorFields, outOfDate, values',
        'Object captured as promise rejection with keys: data, msg, status',
        'TypeError: Failed to fetch',
        'TypeError: d is not a function',
      ];
      const isNonErrorException = messages.some(
        (message) =>
          errMsg?.startsWith(message) ||
          // @ts-ignore
          hint?.originalException?.message?.startsWith(message),
      );

      if (isNonErrorException) {
        return null;
      }

      if (!errMsg) {
        return null;
      }
      // 1. 过滤 非生产上报
      if (!isAllowUrl(event?.request?.url || '')) {
        return null;
      }
      // 2. 过滤 降噪错误
      if (isIgnoreError(errMsg)) {
        return null;
      }
      return event;
    },
  });

  // 监听并上报全局的 unhandledrejection 事件
  window.addEventListener('unhandledrejection', (event) => {
    Sentry.captureException(event.reason, {
      extra: { type: 'unhandledrejection' },
    });
  });
};
